/**
 * What the API server can see of the network: its interfaces, which of them
 * are VPNs, and what the CORS settings make of them. Admin-only on the server.
 */

import { api, type ApiResult } from '@/api/client';

export interface NetworkInterface {
  name: string;
  addresses: string[];
  /** tailscale, netbird, zerotier, wireguard, tailscale_or_netbird, or null. */
  vpnProvider: string | null;
  /**
   * "name" when the interface name says VPN, "address" when it was guessed
   * from a 100.64.0.0/10 address. The second is never as certain as the first.
   */
  vpnEvidence: 'name' | 'address' | null;
  /** Addresses on this interface that CORS_VPN_CIDRS already admits. */
  corsAdmitted: string[];
}

export interface NetworkInfo {
  hostname: string;
  /**
   * The API is in a container with its own network namespace, so the host's
   * VPN interfaces are not visible from it. An empty VPN list then means "not
   * visible from here", not "not there".
   */
  inContainer: boolean;
  interfaces: NetworkInterface[];
  vpn: { interface: string; provider: string | null; evidence: string | null; address: string | null } | null;
  cors: {
    origins: string[];
    publicOrigins: { origin: string; vpnInterface: string | null }[];
    vpnCidrs: string[];
  };
}

export function getNetworkInfo(): Promise<ApiResult<NetworkInfo>> {
  return api.get<NetworkInfo>('/network/info', { logged: false });
}
